import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import server from '../api/server';
import CreateProjectModal from "../dashboard-components/modal/CreateProjectModal"
import NotificationsPanel from "../dashboard-components/dashboard/panel/NotificationsPanel.jsx"
import { LogOut } from 'lucide-react';
import ModernTimeDropdownV2, { getOptionValue } from '../dashboard-components/layouts/modernDropdownv2.jsx';
import TaskStatusComponent from '../dashboard-components/dashboard/panel/TaskStatusComponent.jsx';
import { clearNotificationsAccessTokenLastOkCookie } from '../utils/dashboardNotifAccessTokenCookie.js';

const buildProjectOptions = (projects, legacyServiceIds = []) => {
  if (Array.isArray(projects) && projects.length > 0) {
    return projects.map((project) => ({
      value: project.service_id,
      label: project.project_name || project.service_id,
      hideId: true,
    }));
  }
  return (Array.isArray(legacyServiceIds) ? legacyServiceIds : []).map((serviceId) => ({
    value: serviceId,
    label: serviceId,
  }));
};

const Topbar = ({setSelectedOptions, selectedClientId, selectedServiceId}) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(selectedServiceId || null);
  const [allowClose, setAllowClose] = useState(true)
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  const toggleProfileMenu = () => {
    setIsProfileOpen(!isProfileOpen);
  }

  const [options, setOptions] = useState([]);
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const isMobile = window.innerWidth < 768;
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const location = useLocation();
  const navigate = useNavigate();
  const isVoicePlatform = location.pathname.split('/').filter(Boolean)[0] === 'voice-agents';

  const handleLogout = () => {
    clearNotificationsAccessTokenLastOkCookie();
    document.cookie = "dashboard_login_user_creds=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=None";
    window.dispatchEvent(new Event('storage'));
    window.location.href = '/login';
  };
  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
}
const testLogout = async()=> {
  try {
    let res = await server.logout();
    if(res.status === 200){
      handleLogout();
    }
  } catch (error) {
  }
}
const handleCreateProject = (e) => {
  setSelectedOption(e);
  setSelectedOptions(e);
  setService_id(e);
  setIsModalOpen(false);
  
  // Force refresh the options list to include the newly created project
  setTimeout(() => {
    if (client_id && userEmail && client_id !== 'undefined') {
      getProjectsList();
    }
  }, 500); // Small delay to let the backend process the new project
}
const getProjectsList = async () => {
  try {
    
    let res = await server.getProjectsList(client_id, service_id || selectedServiceId || 'DEFAULT', userEmail, 'home', undefined, role);
    
    if(res.status === 200){
      let resp = await res.json();
      
      // Check if data is available
      if (resp.data && resp.data.length > 0) {
        const projectOptions = buildProjectOptions(resp.projects, resp.data);
        setOptions(projectOptions);
        
        // Check if the URL service ID exists in the options
        const serviceIdExists = projectOptions.some(
          (option) => getOptionValue(option) === selectedServiceId
        );
        
        // If selectedServiceId from URL exists in options, use it
        // Otherwise use the first option
        const optionToSelect = serviceIdExists
          ? selectedServiceId
          : getOptionValue(projectOptions[0]);
        
        setSelectedOption(optionToSelect);
        setSelectedOptions(optionToSelect);
      } else {
        setOptions([]);
        setIsModalOpen(true);
        setAllowClose(false);
      }
    } else {
      // Don't clear options on API failure, keep existing ones
    }
  } catch (error) {
    // Don't clear options on error, keep existing ones
  }
}

useEffect(() => {
  const userCookie = getCookie('dashboard_login_user_creds');  
  if (userCookie) {
    try {
      const user = JSON.parse(userCookie);
      
      // Set client_id from props or cookie
      if (selectedClientId) {
        setClient_id(selectedClientId);
      } else {
        setClient_id(user.client_id);
      }
      
      if (user.service_id) {
        setService_id(user.service_id);
      } else if(selectedServiceId){
        setService_id(selectedServiceId)
      }
      
      // Set user info
      setUsername(user.name);
      setUserEmail(user.email);
      
    } catch (error) {
      console.error("Topbar - Error parsing user data:", error);
    }
  }
}, [selectedClientId, selectedServiceId]);

// Effect to call getProjectsList when any relevant data changes
useEffect(() => {
  
  if (client_id && userEmail && client_id !== 'undefined') {
    getProjectsList();
  }
}, [client_id, userEmail, selectedServiceId]); // Added selectedServiceId to dependencies

// Effect to ensure options are loaded when component mounts or when we detect empty options
useEffect(() => {
  
  // If we have required data but no options, call getProjectsList
  if (client_id && userEmail && client_id !== 'undefined' && options.length === 0) {
    getProjectsList();
  }
}, [options.length, client_id, userEmail]); // Trigger when options.length changes

// Effect to sync selectedOption with selectedServiceId prop changes
useEffect(() => {
  
  // If selectedServiceId changes and we have options available, validate and update
  if (selectedServiceId && options.length > 0) {
    const serviceExists = options.some(
      (option) => getOptionValue(option) === selectedServiceId
    );
    
    if (serviceExists && selectedServiceId !== selectedOption) {
      setSelectedOption(selectedServiceId);
    }
  } else if (selectedServiceId && selectedServiceId !== selectedOption) {
    // Update selectedOption even if options aren't loaded yet
    setSelectedOption(selectedServiceId);
  }
}, [selectedServiceId, options, selectedOption]);

// Fallback effect - ensures options are loaded after component stabilizes
useEffect(() => {
  const fallbackTimer = setTimeout(() => {
    
    if (client_id && userEmail && client_id !== 'undefined' && options.length === 0) {
      getProjectsList();
    }
  }, 1000); // 1 second delay to let everything settle

  return () => clearTimeout(fallbackTimer);
}, [client_id, userEmail, selectedServiceId]); // Re-run when these change

useEffect(() => {
  const refreshDisplayNames = (event) => {
    const detail = event.detail || {};
    if (detail.entity_type !== 'project' || detail.client_id !== client_id) return;
    setOptions((projectOptions) => projectOptions.map((option) => (
      getOptionValue(option) === detail.service_id
        ? { ...option, label: detail.name }
        : option
    )));
  };
  window.addEventListener('displayNamesUpdated', refreshDisplayNames);
  return () => window.removeEventListener('displayNamesUpdated', refreshDisplayNames);
}, [client_id]);

const handleServiceChange = (e) => {
  const newServiceId = e.target.value;
  setSelectedOption(newServiceId);
  setSelectedOptions(newServiceId);
};

// Function to ensure options are loaded - can be called on demand
const ensureOptionsLoaded = () => {
  
  if (client_id && userEmail && client_id !== 'undefined' && options.length === 0) {
    getProjectsList();
  }
};

  return (
    <div className={`${location.pathname.startsWith('/voice-agents/') ? 'flex-none' : 'flex-1'} overflow-visible`} style={{height: "4rem"}}>
    {/* Top bar with org name and actions - Fixed position */}
    <div className="bg-white h-16 px-6 flex items-center justify-between border-b border-gray-200 sticky top-0 z-10">
      <div className="text-gray-700 font-medium">
        {/* Service Selection Dropdown - Moved to left */}
        {!isVoicePlatform && (
          <div className="relative" style={{marginRight: (!isMobile) ? "1rem":""}} onClick={ensureOptionsLoaded}>
            <ModernTimeDropdownV2 
              placeholder="Select Service ID" 
              value={selectedOption} 
              onChange={handleServiceChange} 
              options={options}
            />
          </div>
        )}
      </div>
      
      <div className="flex items-center">
        {/* Create Project Button - Using rgb(79, 70, 229) */}
        {role !== 'employee' && (
          <button 
            className="mr-6 px-4 py-2 text-white font-medium text-sm rounded-md shadow-sm flex items-center justify-center h-9" 
            style={{ backgroundColor: 'rgb(79, 70, 229)', ":hover": { backgroundColor: '#4338ca' }, fontWeight: "600", fontSize: "14px", borderRadius: "4px" }} 
            onClick={() => {
              if (isVoicePlatform) {
                const svc = selectedServiceId || service_id || selectedOption || 'DEFAULT';
                const cid = selectedClientId || client_id;
                if (cid && svc) {
                  navigate(`/voice-agents/${cid}/${svc}/agents?action=create`, { state: { openCreate: true } });
                }
              } else {
                setIsModalOpen(true);
              }
            }}
          >
            {isVoicePlatform ? 'Create Agent' : 'Create Project'}
          </button>
        )}
        
        {/* Group notifications and profile together */}
        <div className="flex items-center space-x-5">
          <TaskStatusComponent serviceID={selectedOption} clientID={client_id} />
          {/* Notifications */}
          <NotificationsPanel serviceID={selectedOption} clientID={client_id} />
          
          {/* User Profile */}
          <div className="flex items-center relative">
            <div>
              <button 
                className="flex items-center justify-center"
                onClick={toggleProfileMenu}
              >
                {/* Avatar with First Letter */}
                {username && <div className="w-9 h-9 rounded-full bg-black flex items-center justify-center text-white font-medium">
                  {username.split('')[0].toUpperCase()}
                </div>}
              </button>
              
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div style={{display: "flex", alignItems: "center"}}>
                    <p className="text-sm font-medium text-gray-700">{username}</p>
                    <span className="ml-2 px-1.5 py-0.5 text-xs text-gray-500 border border-gray-300 rounded capitalize">{role}</span></div>
                    <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                  </div>
                  <div className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer" onClick={testLogout}>
                    <LogOut size={16} className="mr-2" />
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    {isModalOpen && <CreateProjectModal onClose={() => setIsModalOpen(false)} onCreateProject={handleCreateProject} clientID={client_id} email={userEmail} allow={allowClose}/>}
    </div>
  );
};

export default Topbar;
